import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { DG } from '@cotera/sdk/core';
import { InputShapeNodeView, type InputShapeRow } from './view';
import type { ExtensionMenuConfig } from '@cotera/client/app/components/app/rte/types/extension-config';
import type { SlashCommand } from '@cotera/client/app/components/app/rte/types/extension-interface';

export interface InputShapeOptions extends ExtensionMenuConfig {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    inputShape: {
      setInputShape: (attributes?: { fields?: InputShapeRow[] }) => ReturnType;
    };
  }
}

const DEFAULT_FIELDS: InputShapeRow[] = [
  { name: '', type: 'string', description: null },
];

export const InputShapeNode = Node.create<InputShapeOptions>({
  name: DG.INPUT_SHAPE_NODE_TYPE,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  atom: true,
  selectable: false,

  addAttributes() {
    return {
      fields: {
        default: DEFAULT_FIELDS,
        parseHTML: (element) => {
          const data = element.getAttribute('data-fields');
          if (data === null) {
            return DEFAULT_FIELDS;
          }
          try {
            return JSON.parse(data);
          } catch {
            return DEFAULT_FIELDS;
          }
        },
        renderHTML: (attributes) => ({
          'data-fields': JSON.stringify(attributes['fields']),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="input-shape"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'input-shape' }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InputShapeNodeView);
  },

  addCommands() {
    return {
      setInputShape:
        (attributes = {}) =>
        ({ commands }) => {
          commands.insertContent(
            { type: this.name, attrs: attributes },
            { updateSelection: false }
          );
          return commands.setTextSelection(-1);
        },
    };
  },

  addStorage() {
    return {
      getSlashConfig: (): SlashCommand => ({
        title: 'Inputs',
        subtext: 'Define the named inputs this agent takes',
        keywords: ['input', 'inputs', 'parameters', 'params', 'arguments'],
        badge: 'code-block',
        group: 'Insert',
        available: (editor) => {
          if (!editor.isEditable || !editor.can().setInputShape()) {
            return false;
          }
          // One per document: the agent has a single input shape.
          let hasInputShape = false;
          editor.state.doc.descendants((node) => {
            if (node.type.name === DG.INPUT_SHAPE_NODE_TYPE) {
              hasInputShape = true;
              return false;
            }
            return true;
          });
          return !hasInputShape;
        },
        action: ({ editor }) => {
          editor.chain().focus().setInputShape().run();
        },
      }),
    };
  },
});
